import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Gift, Plus, Edit3, Trash2, Star, X, QrCode, CheckCircle2 } from 'lucide-react';
import { clubsService } from '../../../services';
import MemberPassScannerModal from '../../../components/memberPass/MemberPassScannerModal';
import './ManageClubs.css';
import VenueSubnav from './VenueSubnav';

const emptyForm = {
    title: '',
    shortDescription: '',
    fullDescription: '',
    offerType: 'General',
    audience: 'Public',
    redemptionType: 'Code',
    offerCode: '',
    termsAndConditions: '',
    ctaLabel: '',
    ctaUrl: '',
    imageUrl: '',
    startDate: '',
    endDate: '',
    isActive: true,
    isFeatured: false
};

const ManageOffers = () => {
    const { clubId } = useParams();
    const navigate = useNavigate();
    const [club, setClub] = useState(null);
    const [offers, setOffers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingOffer, setEditingOffer] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [claimOffer, setClaimOffer] = useState(null);
    const [claimToken, setClaimToken] = useState('');
    const [claimNotes, setClaimNotes] = useState('');
    const [isClaimSubmitting, setIsClaimSubmitting] = useState(false);
    const [claimFeedback, setClaimFeedback] = useState(null);
    const [claimsByOffer, setClaimsByOffer] = useState({});
    const [claimsLoadingByOffer, setClaimsLoadingByOffer] = useState({});
    const [showClaimScanner, setShowClaimScanner] = useState(false);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [clubData, offerData] = await Promise.all([
                clubsService.getClub(clubId),
                clubsService.getClubOffers(clubId)
            ]);
            setClub(clubData);
            setOffers(offerData);
        } catch (err) {
            setError(err.message || 'Failed to load venue offers.');
        } finally {
            setIsLoading(false);
        }
    }, [clubId]);

    useEffect(() => {
        load();
    }, [load]);

    const sortedOffers = useMemo(() => [...offers].sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured)), [offers]);

    const openCreate = () => {
        setEditingOffer(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (offer) => {
        setEditingOffer(offer);
        setForm({
            title: offer.title || '',
            shortDescription: offer.shortDescription || '',
            fullDescription: offer.fullDescription || '',
            offerType: offer.offerType || 'General',
            audience: offer.audience || 'Public',
            redemptionType: offer.redemptionType || 'Code',
            offerCode: offer.offerCode || '',
            termsAndConditions: offer.termsAndConditions || '',
            ctaLabel: offer.ctaLabel || '',
            ctaUrl: offer.ctaUrl || '',
            imageUrl: offer.imageUrl || '',
            startDate: offer.startDate?.slice(0, 10) || '',
            endDate: offer.endDate?.slice(0, 10) || '',
            isActive: offer.isActive,
            isFeatured: offer.isFeatured
        });
        setShowModal(true);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setError(null);
        try {
            const payload = {
                ...form,
                startDate: form.startDate || null,
                endDate: form.endDate || null
            };

            if (editingOffer) {
                await clubsService.updateClubOffer(clubId, editingOffer.clubOfferId, payload);
            } else {
                await clubsService.createClubOffer(clubId, payload);
            }

            setShowModal(false);
            await load();
        } catch (err) {
            setError(err.message || 'Failed to save venue offer.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (offer) => {
        if (!window.confirm(`Delete "${offer.title}"?`)) {
            return;
        }

        try {
            await clubsService.deleteClubOffer(clubId, offer.clubOfferId);
            await load();
        } catch (err) {
            setError(err.message || 'Failed to delete offer.');
        }
    };

    const loadClaims = useCallback(async (offerId) => {
        setClaimsLoadingByOffer((prev) => ({ ...prev, [offerId]: true }));
        try {
            const data = await clubsService.getClubPerkClaims(clubId, offerId);
            setClaimsByOffer((prev) => ({ ...prev, [offerId]: Array.isArray(data) ? data : [] }));
        } catch (err) {
            setError(err.message || 'Failed to load Connect Perk claims.');
        } finally {
            setClaimsLoadingByOffer((prev) => ({ ...prev, [offerId]: false }));
        }
    }, [clubId]);

    const openClaimModal = async (offer) => {
        setClaimOffer(offer);
        setClaimToken('');
        setClaimNotes('');
        setClaimFeedback(null);
        setShowClaimModal(true);
        await loadClaims(offer.clubOfferId);
    };

    const handleClaimSubmit = async () => {
        if (!claimOffer || !claimToken.trim()) {
            return;
        }

        setIsClaimSubmitting(true);
        setClaimFeedback(null);
        try {
            const result = await clubsService.claimClubPerk(clubId, claimOffer.clubOfferId, {
                memberPassToken: claimToken.trim(),
                notes: claimNotes.trim() || null
            });
            setClaimFeedback(result?.message || 'Connect Perk claimed successfully.');
            setClaimToken('');
            setClaimNotes('');
            await loadClaims(claimOffer.clubOfferId);
        } catch (err) {
            setClaimFeedback(err.message || 'Failed to claim Connect Perk.');
        } finally {
            setIsClaimSubmitting(false);
        }
    };

    const handleClaimPassDetected = (value) => {
        setClaimToken(value);
        setShowClaimScanner(false);
        setClaimFeedback('Member pass scanned. You can claim this perk now.');
    };

    if (isLoading) {
        return (
            <div className="manage-clubs-page">
                <div className="manage-clubs-loading">
                    <div className="manage-clubs-spinner"></div>
                    <p>Loading offers...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-clubs-page">
            <button className="club-dashboard-back" onClick={() => navigate(`/dashboard/club/${clubId}/dashboard`)}>
                <ChevronLeft size={18} />
                Back to {club?.name || 'Venue'} Venue Dashboard
            </button>

            <VenueSubnav clubId={clubId} />

            <div className="manage-section-header">
                <div className="manage-section-header-content">
                    <div className="manage-section-header-icon icon-orange">
                        <Gift size={24} />
                    </div>
                    <div>
                        <h1>Connect Perks</h1>
                        <p>Create discoverable offers and claim perks with a member QR pass at {club?.name}</p>
                    </div>
                </div>
                <button className="manage-section-add-btn" onClick={openCreate}>
                    <Plus size={18} />
                    Add Perk
                </button>
            </div>

            {error && <div className="manage-clubs-error">{error}</div>}

            {sortedOffers.length === 0 ? (
                <div className="manage-clubs-empty">
                    <div className="manage-clubs-empty-icon">
                        <Gift size={48} />
                    </div>
                    <h3>No Connect Perks Yet</h3>
                    <p>Add your first venue offer, taster session, discount, or member perk.</p>
                </div>
            ) : (
                <div className="members-list">
                    {sortedOffers.map((offer) => (
                        <div key={offer.clubOfferId} className="member-card">
                            <div className="member-avatar">
                                <Gift size={18} />
                            </div>
                            <div className="member-info">
                                <h4>
                                    {offer.title} {offer.isFeatured && <Star size={14} style={{ color: '#FF6A00', display: 'inline' }} />}
                                </h4>
                                <p>{offer.shortDescription || offer.fullDescription || 'No description added yet.'}</p>
                                <span className="member-username">{offer.audience} · {offer.offerType} · {offer.isActive ? 'Active' : 'Inactive'}</span>
                            </div>
                            <div className="member-actions connect-perk-actions">
                                <button className="member-action-btn" onClick={() => openClaimModal(offer)} title="Claim Connect Perk">
                                    <QrCode size={18} />
                                </button>
                                <button className="member-action-btn" onClick={() => openEdit(offer)}>
                                    <Edit3 size={18} />
                                </button>
                                <button className="member-action-btn" onClick={() => handleDelete(offer)}>
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingOffer ? 'Edit Offer' : 'Add Offer'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Title</label>
                                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Short Description</label>
                                <input value={form.shortDescription} onChange={(e) => setForm({ ...form, shortDescription: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Full Description</label>
                                <textarea value={form.fullDescription} onChange={(e) => setForm({ ...form, fullDescription: e.target.value })} rows={4} />
                            </div>
                            <div className="form-group">
                                <label>Offer Type</label>
                                <select value={form.offerType} onChange={(e) => setForm({ ...form, offerType: e.target.value })}>
                                    <option value="General">General</option>
                                    <option value="Discount">Discount</option>
                                    <option value="Taster">Taster Session</option>
                                    <option value="Event">Event Offer</option>
                                    <option value="MemberPerk">Member Perk</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Audience</label>
                                <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                                    <option value="Public">Public</option>
                                    <option value="AllMembers">AllMembers</option>
                                    <option value="PremiumMembers">PremiumMembers</option>
                                    <option value="ClubMembers">ClubMembers</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Offer Code</label>
                                <input value={form.offerCode} onChange={(e) => setForm({ ...form, offerCode: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Call To Action Label</label>
                                <input value={form.ctaLabel} onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Call To Action URL</label>
                                <input value={form.ctaUrl} onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Image URL</label>
                                <input value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Terms & Conditions</label>
                                <textarea value={form.termsAndConditions} onChange={(e) => setForm({ ...form, termsAndConditions: e.target.value })} rows={3} />
                            </div>
                            <div className="form-group">
                                <label>Start Date</label>
                                <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>End Date</label>
                                <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
                                    Active
                                </label>
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })} />
                                    Featured Offer
                                </label>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                            <button className="btn-primary" onClick={handleSubmit} disabled={!form.title || isSubmitting}>
                                {isSubmitting ? 'Saving...' : editingOffer ? 'Update Offer' : 'Create Offer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showClaimModal && claimOffer && (
                <div className="modal-overlay" onClick={() => setShowClaimModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Claim Connect Perk</h2>
                            <button className="modal-close" onClick={() => setShowClaimModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="connect-perk-claim-header">
                                <h3>{claimOffer.title}</h3>
                                <p>Paste the member pass token from their QR code to register this perk claim.</p>
                            </div>

                            <div className="form-group">
                                <label>Member Pass Token</label>
                                <input
                                    value={claimToken}
                                    onChange={(e) => setClaimToken(e.target.value)}
                                    placeholder="SC_MEMBER_PASS:..."
                                />
                                <button
                                    type="button"
                                    className="connect-perk-scan-btn"
                                    onClick={() => setShowClaimScanner(true)}
                                >
                                    Scan Member Pass QR
                                </button>
                            </div>

                            <div className="form-group">
                                <label>Notes (Optional)</label>
                                <textarea
                                    value={claimNotes}
                                    onChange={(e) => setClaimNotes(e.target.value)}
                                    rows={3}
                                    placeholder="Example: Claimed at front desk during open day."
                                />
                            </div>

                            {claimFeedback && (
                                <div className="connect-perk-claim-feedback">
                                    <CheckCircle2 size={16} />
                                    <span>{claimFeedback}</span>
                                </div>
                            )}

                            <div className="connect-perk-claims-panel">
                                <h4>Recent Claims</h4>
                                {claimsLoadingByOffer[claimOffer.clubOfferId] ? (
                                    <p className="connect-perk-muted">Loading claims...</p>
                                ) : (claimsByOffer[claimOffer.clubOfferId] || []).length === 0 ? (
                                    <p className="connect-perk-muted">No claims yet for this perk.</p>
                                ) : (
                                    <div className="connect-perk-claims-list">
                                        {(claimsByOffer[claimOffer.clubOfferId] || []).slice(0, 8).map((claim) => (
                                            <div key={claim.clubPerkClaimId} className="connect-perk-claim-item">
                                                <strong>{claim.userName || claim.email || `Member #${claim.userId}`}</strong>
                                                <span>{new Date(claim.claimedAt).toLocaleString()}</span>
                                                <span>
                                                    Claimed by: {claim.claimedByUserName || `User #${claim.claimedByUserId}`}
                                                    {claim.passReferencePreview ? ` · Pass ${claim.passReferencePreview}` : ''}
                                                </span>
                                                {claim.notes ? <span>Notes: {claim.notes}</span> : null}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowClaimModal(false)}>Close</button>
                            <button
                                className="btn-primary"
                                onClick={handleClaimSubmit}
                                disabled={!claimToken.trim() || isClaimSubmitting}
                            >
                                {isClaimSubmitting ? 'Claiming...' : 'Claim Perk'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <MemberPassScannerModal
                isOpen={showClaimScanner}
                onClose={() => setShowClaimScanner(false)}
                onDetected={handleClaimPassDetected}
                title="Scan Member Pass for Claim"
                description="Scan the member profile QR to register this Connect Perk claim."
            />
        </div>
    );
};

export default ManageOffers;
